import React, { useEffect, useMemo, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import { SportEvent } from '../types/SportEventTypes';
import ClipGroupItem from './ClipGroupItem';
import { getFeaturedParticipantIds } from '../sports/registry';
import { useFOVOverlay } from '../context/FOVOverlayContext';
import type { ClipCameraSelection } from '@/features/clips/types';

interface Camera {
  id: number;
  displayName: string;
}

interface ClipGroup {
  id: string | number;
  trackedObjectId?: string | null;
  inTime?: Date | string;
  outTime?: Date | string;
  isPersisted?: boolean;
  isCreating?: boolean;
  persistedId?: string | number;
  clips?: Array<{ cameraId: number | null; autoDirector?: boolean }>;
  outputProfileId?: number | null; // Transcoding profile assignment ID
}

interface ClipCreationFormProps {
  clipGroups: ClipGroup[];
  event: SportEvent;
  isShiftClip?: boolean;
  availableCameras: Camera[];
  onTimeChange: (groupId: string | number, field: 'inTime' | 'outTime', time: Date) => void;
  onCameraClick: (groupId: string | number, camera: ClipCameraSelection) => void;
  onDeleteClipGroup: (groupId: string | number) => void;
  onTrackedObjectChange: (groupId: string, entityId: string | null) => Promise<void>;
  onOutputModeChange?: (
    groupId: string | number,
    outputMode: 'portrait' | 'landscape',
  ) => Promise<void>;
}

export const ClipCreationForm: React.FC<ClipCreationFormProps> = ({
  clipGroups,
  event,
  availableCameras,
  onTimeChange,
  onCameraClick,
  onDeleteClipGroup,
  onTrackedObjectChange,
  onOutputModeChange,
  isShiftClip = false,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const { setActivePortraitGroupId } = useFOVOverlay();

  useEffect(() => {
    const activeGroup = clipGroups.find((group) => {
      const resolvedId =
        'persistedId' in group && group.persistedId ? group.persistedId : group.id;
      return String(resolvedId) === activeGroupId || String(group.id) === activeGroupId;
    });

    if (activeGroup) {
      // A just-persisted draft keeps its draft id while items address it by
      // persistedId — migrate the active id instead of treating it as gone.
      const canonicalId =
        'persistedId' in activeGroup && activeGroup.persistedId
          ? String(activeGroup.persistedId)
          : String(activeGroup.id);
      if (canonicalId !== activeGroupId) {
        setActiveGroupId(canonicalId);
      }
      return;
    }

    const firstGroup = clipGroups[0];
    const firstGroupId = firstGroup
      ? 'persistedId' in firstGroup && firstGroup.persistedId
        ? firstGroup.persistedId
        : firstGroup.id
      : null;
    if (firstGroupId == null) {
      // The active group's item unmounted without clearing the overlay, and
      // with no replacement item the overlay-sync effect never runs again.
      setActivePortraitGroupId(null);
    }
    setActiveGroupId(firstGroupId == null ? null : String(firstGroupId));
  }, [activeGroupId, clipGroups, setActivePortraitGroupId]);

  // Featured players are resolved by the per-sport module that owns the
  // event's type (NHL: shooter/assister/…; NBA: shooter/player/…). Keying this
  // off the sport registry — rather than a hockey-only switch here — is what
  // makes the "Featured in Event" shortcuts light up for NBA, not just NHL.
  const featuredPlayers = useMemo(() => getFeaturedParticipantIds(event), [event]);

  if (clipGroups.length === 0) {
    if (isShiftClip) {
      return <></>;
    }

    return (
      <Box
        sx={{
          border: `1px dashed ${theme.palette.cx.border.default}`,
          mt: 1.5,
          mb: 1,
          mx: 1.5,
          py: 2,
          px: 8,
          borderRadius: 1,
          textAlign: 'center',
          color: theme.palette.cx.light[200],
          fontSize: '12px',
          fontWeight: 400,
          lineHeight: '16px',
        }}
      >
        There are currently no clips to display
      </Box>
    );
  }

  return (
    <>
      {clipGroups.map((group, index) => (
        <ClipGroupItem
          key={'persistedId' in group && group.persistedId ? group.persistedId : group.id}
          group={group}
          index={index}
          event={event}
          availableCameras={availableCameras}
          featuredPlayers={featuredPlayers}
          onTimeChange={onTimeChange}
          onCameraClick={onCameraClick}
          onDeleteClipGroup={onDeleteClipGroup}
          onTrackedObjectChange={onTrackedObjectChange}
          onOutputModeChange={onOutputModeChange}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          activeGroupId={activeGroupId}
          setActiveGroupId={setActiveGroupId}
        />
      ))}
    </>
  );
};
